import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useAppSelector } from "@/app/hooks";
import { basketClient } from "@/core/api";
import { BasketOrder, BasketQuote } from "@/core/api/basket";
import { selectUserInfo } from "@/core/store/authSlice";
import { PurchaserDetails } from "@/components/CustomiseForm/PurchaserDetails/PurchaserDetails";
import { Address } from "components/Address/Address";
import { FormInput } from "components/basic/FormInput";
import { ButtonRound } from "components/basic/Buttons";
import { validationSchemaKeeperOptional } from "../customise/validationSchema";

// Multi-plate basket checkout, port of the privateplates checkout.
// BETA ONLY, by hostname allow-list (same reasoning as WALLET_PAY_ENABLED in
// CheckoutPayment.tsx: an allow-list cannot leak onto an unexpected host).
// The server half is gated separately by BASKET_CHECKOUT_ENABLED.
export const BASKET_ENABLED =
  typeof window !== "undefined" &&
  ["beta.newreg.co.uk", "localhost"].includes(window.location.hostname);

const STORAGE_KEY = "nr-basket-1";
const MAX_LINES = 9;

type Step = "basket" | "details" | "placed";

const compact = (reg: string) => reg.toUpperCase().replace(/[^A-Z0-9]/g, "");

const readBasket = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((r) => typeof r === "string") : [];
  } catch (e) {
    return [];
  }
};

const writeBasket = (regs: string[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(regs));
  } catch (e) {
    // Private mode: the basket lives for this page only.
  }
};

const money = (n: number) => `£${n.toFixed(2)}`;

export const CheckoutBasket = () => {
  const userInfo = useAppSelector(selectUserInfo);
  const [regs, setRegs] = useState<string[]>(() => readBasket());
  const [quote, setQuote] = useState<BasketQuote | null>(null);
  const [quoteError, setQuoteError] = useState("");
  const [step, setStep] = useState<Step>("basket");
  const [keepers, setKeepers] = useState<Record<string, { year: string; vehicle_keeper: string }>>({});
  const [placed, setPlaced] = useState<BasketOrder | null>(null);
  const [submitError, setSubmitError] = useState("");

  // ?add=P2XYP adds a plate (the entry point from plate pages, for now).
  useEffect(() => {
    const add = new URLSearchParams(window.location.search).get("add");
    if (add) {
      setRegs((current) => {
        const reg = compact(add);
        if (!reg || current.includes(reg) || current.length >= MAX_LINES) return current;
        return [...current, reg];
      });
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  useEffect(() => writeBasket(regs), [regs]);

  useEffect(() => {
    if (!BASKET_ENABLED || regs.length === 0) {
      setQuote(null);
      return;
    }
    let live = true;
    setQuoteError("");
    basketClient
      .quote(regs.map((registration) => ({ registration })))
      .then((res) => live && setQuote(res.data))
      .catch(() => live && setQuoteError("We couldn't price your basket. Please try again."));
    return () => {
      live = false;
    };
  }, [regs]);

  const available = useMemo(() => (quote ? quote.lines.filter((l) => l.available) : []), [quote]);

  const remove = useCallback((reg: string) => {
    setRegs((current) => current.filter((r) => r !== compact(reg)));
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: (userInfo?.user as string) || "",
      title: (userInfo?.title as string) || "",
      forename: (userInfo?.forename as string) || "",
      surname: (userInfo?.surname as string) || "",
      telephone1: (userInfo?.telephone1 as string) || "",
      address1: (userInfo?.address1 as string) || "",
      address2: (userInfo?.address2 as string) || "",
      address3: (userInfo?.address3 as string) || "",
      address4: (userInfo?.address4 as string) || "",
      postcode: userInfo?.postcode ? (userInfo.postcode as string).toUpperCase() : "",
      isManualAddress: !!userInfo?.address1,
    },
    validationSchema: validationSchemaKeeperOptional,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitError("");
      const { isManualAddress, ...buyer } = values;
      basketClient
        .order({
          buyer: buyer as unknown as Record<string, string>,
          lines: available.map((l) => ({
            registration: l.registration,
            year: keepers[compact(l.registration)]?.year || "",
            vehicle_keeper: keepers[compact(l.registration)]?.vehicle_keeper || "",
          })),
        })
        .then((res) => {
          setPlaced(res.data);
          setStep("placed");
          window.scrollTo(0, 0);
        })
        .catch((err) => {
          setSubmitError(err?.response?.data?.message || err?.message || "Something went wrong. Please try again.");
        })
        .finally(() => setSubmitting(false));
    },
  });

  if (!BASKET_ENABLED) {
    return (
      <div className="container mx-auto p-6 text-left">
        <p>This page is not available.</p>
      </div>
    );
  }

  const setKeeper = (reg: string, field: "year" | "vehicle_keeper", value: string) =>
    setKeepers((k) => ({ ...k, [compact(reg)]: { ...{ year: "", vehicle_keeper: "" }, ...k[compact(reg)], [field]: value } }));

  const { values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting } = formik;

  return (
    <div className="container mx-auto p-4 md:p-6 text-left max-w-3xl">
      {step === "basket" && (
        <>
          <h1 className="font-bold text-2xl mb-4">Your basket</h1>
          {regs.length === 0 && <p>Your basket is empty.</p>}
          {quoteError && <p className="text-red-600 mb-3">{quoteError}</p>}
          {quote && (
            <ul className="divide-y border rounded mb-4">
              {quote.lines.map((line) => (
                <li key={line.registration} className="flex items-center justify-between p-3 gap-3">
                  <span className="font-bold text-lg whitespace-nowrap">{line.registration}</span>
                  <span className="flex-1 text-sm text-gray-600">
                    {/* The VAT amount and the fee stated, as privateplates' basket
                        line does since 15 Oct 2026 (privateplates 460e189). A
                        VAT-free dealer plate says so rather than "VAT £0.00". */}
                    {line.available
                      ? line.registrationDetails
                        ? Number(line.registrationDetails.display_vat) > 0
                          ? `Includes VAT £${line.registrationDetails.display_vat} & DVLA fee £${line.registrationDetails.transfer_fee}`
                          : `No VAT. Includes DVLA fee £${line.registrationDetails.transfer_fee}`
                        : "Includes VAT & DVLA fee"
                      : "No longer available"}
                  </span>
                  <span className="font-bold whitespace-nowrap">{line.available ? money(line.total) : ""}</span>
                  <button type="button" className="text-sm underline" onClick={() => remove(line.registration)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          {quote && available.length > 0 && (
            <>
              <p className="text-right text-xl font-bold mb-4">Total {money(quote.total)}</p>
              <ButtonRound text="Continue" buttonType="button" onClick={() => setStep("details")} />
            </>
          )}
        </>
      )}

      {step === "details" && (
        <form onSubmit={formik.handleSubmit} noValidate>
          <button type="button" className="text-sm underline mb-3" onClick={() => setStep("basket")}>
            Back to basket
          </button>
          <h1 className="font-bold text-2xl mb-4">Your details</h1>
          <PurchaserDetails
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            handleBlur={handleBlur}
          />
          <h2 className="font-bold text-xl mt-8 mb-3">Address details</h2>
          <Address
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            handleBlur={handleBlur}
            selectErrorMsg={touched?.isManualAddress ? (errors?.isManualAddress as string) : ""}
          />
          <h2 className="font-bold text-xl mt-8 mb-1">Registered keeper</h2>
          <p className="text-sm text-gray-600 mb-3">
            Who the plate will be registered to. You can add this later if you don't know it yet.
          </p>
          {available.map((line) => (
            <div key={line.registration} className="border rounded p-3 mb-3">
              <p className="font-bold mb-2">{line.registration}</p>
              <FormInput
                label="Registered keeper's name"
                name={`keeper_${compact(line.registration)}`}
                value={keepers[compact(line.registration)]?.vehicle_keeper || ""}
                maxLength={50}
                onChange={(e) => setKeeper(line.registration, "vehicle_keeper", e.currentTarget.value)}
              />
            </div>
          ))}
          {submitError && <p className="text-red-600 my-3">{submitError}</p>}
          <p className="text-right text-xl font-bold my-4">Total {money(quote ? quote.total : 0)}</p>
          <ButtonRound text={isSubmitting ? "Saving..." : "Continue to payment"} buttonType="submit" disabled={isSubmitting} />
        </form>
      )}

      {step === "placed" && placed && (
        <>
          <h1 className="font-bold text-2xl mb-4">Order saved</h1>
          <p className="mb-3">Basket reference {placed.basketRef}. One order record per plate:</p>
          <ul className="divide-y border rounded mb-4">
            {placed.orders.map((o) => (
              <li key={o.formId} className="flex justify-between p-3">
                <span className="font-bold">{o.registration}</span>
                <span>{money(o.price)}</span>
              </li>
            ))}
          </ul>
          {placed.unavailable.length > 0 && (
            <p className="mb-3">No longer available, so not ordered: {placed.unavailable.join(", ")}</p>
          )}
          <p className="text-sm text-gray-600">Beta build: payment is the next step to be added.</p>
        </>
      )}
    </div>
  );
};

export default CheckoutBasket;
